import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Envelope, Lock, User, GoogleLogo, Sun, Moon, ArrowRight, Gift } from '@phosphor-icons/react';

export default function RegisterPage() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [referralCode, setReferralCode] = useState('');
  const { register, loginWithGoogle } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    // Capture ?ref= from URL or from localStorage (persisted from landing)
    const fromUrl = searchParams.get('ref');
    const fromStorage = localStorage.getItem('pending_referral_code');
    const code = fromUrl || fromStorage || '';
    if (code) {
      setReferralCode(code.toUpperCase());
      if (fromUrl) localStorage.setItem('pending_referral_code', fromUrl.toUpperCase());
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    const result = await register(email, password, name, referralCode || undefined);
    if (result.success) {
      if (referralCode) localStorage.removeItem('pending_referral_code');
      navigate('/dashboard');
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex flex-col justify-center px-8 lg:px-16 xl:px-24">
        <div className="w-full max-w-md mx-auto">
          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className="absolute top-6 right-6 p-2 rounded-sm border border-border hover:bg-accent transition-colors"
            data-testid="theme-toggle"
          >
            {theme === 'light' ? <Moon size={20} /> : <Sun size={20} />}
          </button>

          {/* Logo */}
          <div className="mb-8">
            <div className="h-12 w-14 flex items-center justify-center text-white font-heading font-bold text-lg mb-3" style={{ background: '#002677' }}>
              BIL
            </div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">BIL</h1>
            <p className="text-sm text-muted-foreground mt-1">Business Intelligence Lounge</p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <h2 className="font-heading text-2xl font-semibold tracking-tight">Create account</h2>
              <p className="text-sm text-muted-foreground mt-1">Get started with your free account</p>
            </div>

            {error && (
              <div className="p-3 text-sm bg-destructive/10 text-destructive border border-destructive/20 rounded-sm" data-testid="register-error">
                {error}
              </div>
            )}

            {referralCode && (
              <div className="flex items-center gap-2 p-3 text-sm bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-500/20 rounded-sm" data-testid="referral-banner">
                <Gift size={16} />
                <span>You were invited via referral code <strong className="font-mono">{referralCode}</strong></span>
              </div>
            )}

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                  Full Name
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-10 rounded-sm"
                    placeholder="John Doe"
                    required
                    data-testid="register-name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                  Email
                </Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 rounded-sm"
                    placeholder="you@company.com"
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-xs tracking-[0.2em] uppercase font-semibold text-muted-foreground">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 rounded-sm"
                    placeholder="Min 6 characters"
                    required
                    data-testid="register-password"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full rounded-sm font-medium"
              disabled={loading}
              data-testid="register-submit"
            >
              {loading ? 'Creating account...' : 'Create Account'}
              <ArrowRight className="ml-2" size={18} />
            </Button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-background px-2 text-muted-foreground">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full rounded-sm"
              onClick={loginWithGoogle}
              data-testid="google-register"
            >
              <GoogleLogo className="mr-2" size={18} weight="bold" />
              Google
            </Button>

            <p className="text-center text-sm text-muted-foreground">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">
                Sign in
              </Link>
            </p>
          </form>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src={theme === 'dark' 
            ? "https://static.prod-images.emergentagent.com/jobs/767ee0a6-f97a-4e8b-8d7e-a2366b4bd8cb/images/83b6e714d7ed4ec6e73ddb9ff02358205a1e81dfdb2469048af35657a3f997b7.png"
            : "https://static.prod-images.emergentagent.com/jobs/767ee0a6-f97a-4e8b-8d7e-a2366b4bd8cb/images/6efbde9b2c787d455e9ca44a1a10270ce116f6a6dfab2f3c9d9d79dd0158b271.png"
          }
          alt="Corporate abstract"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-transparent" />
      </div>
    </div>
  );
}
